import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  blockParticipant,
  clearAdminKey,
  deleteMessage,
  drawBuyer,
  errorMessage,
  fetchPanelState,
  isUnauthorized,
  launchJackpot,
  loadAdminKey,
  saveAdminKey,
  updateDisplay,
  updateStopwatch,
} from '../../api';
import { drawFromRecord, pad, withDraw } from '../../draws';
import Games from '../Games';
import Scoreboard from '../Scoreboard';
import Stopwatch from '../Stopwatch';
import ReviewQueue from './ReviewQueue';
import ScoreboardControls from './ScoreboardControls';
import UsersReport from './UsersReport';
import useModeratorRoom from './useModeratorRoom';

import './Admin.css';

const REFRESH_MS = 10000; // people keep registering, buying and writing while the panel is open
const CONFIRM_MS = 6000; // a draw is live: it needs a second tap to go
const DEFAULT_RANGE = { from: 1, to: 300 }; // bandana numbers, until the admin types the real ones
const MAX_NUMBER = 999;

const time = (iso) => new Date(iso).toLocaleTimeString('es-BO', { hour: '2-digit', minute: '2-digit' });

// Under each piece of the stage: whether the phones see it, and the switch that changes it.
// The panel itself always shows it, dimmed while it is hidden
const DisplayToggle = ({ name, visible, busy, onToggle }) => (
  <p className='stage-piece__display'>
    <span className={`stage-piece__state${visible ? '' : ' stage-piece__state--hidden'}`}>
      {visible ? 'Visible para los usuarios' : 'Oculto para los usuarios'}
    </span>
    <button
      type='button'
      role='switch'
      aria-checked={visible}
      aria-label={`Mostrar ${name} a los usuarios`}
      className='admin__signout'
      disabled={busy}
      onClick={onToggle}
    >
      {visible ? 'Ocultar para usuarios' : 'Mostrar a los usuarios'}
    </button>
  </p>
);

const Panel = ({ adminKey, state, refresh, signOut, error, setError, emotes }) => {
  const [confirming, setConfirming] = useState(null); // 'jackpot' | 'buyers' while the second tap is awaited
  const [launching, setLaunching] = useState(null); // same, while the backend draws
  const [range, setRange] = useState(DEFAULT_RANGE);
  const rangeTouched = useRef(false);
  const confirmTimer = useRef(null);

  const onUnauthorized = useCallback(() => signOut(), [signOut]);
  const { messages, status, draws, scoreboard, display, stopwatch, dropMessage, dropPhoto, clearMessages, announce, syncScoreboard, syncDisplay, syncStopwatch } =
    useModeratorRoom(adminKey, onUnauthorized);
  const [timing, setTiming] = useState(false); // the stopwatch is being started, paused or reset
  const [resetting, setResetting] = useState(false); // resetting the stopwatch awaits a second tap
  const [timingError, setTimingError] = useState(null); // shown right under the stopwatch: a tap that did nothing has to say why there
  const [switching, setSwitching] = useState(null); // the piece being hidden or shown, while the backend answers

  useEffect(() => () => clearTimeout(confirmTimer.current), []);

  // The scoreboard as it stood when the panel opened; from then on the room keeps it up to date
  const stateScoreboard = state?.scoreboard;
  useEffect(() => {
    syncScoreboard(stateScoreboard);
  }, [stateScoreboard, syncScoreboard]);

  // And what the phones show: the scoreboard and the draws can be hidden from them
  const stateDisplay = state?.display;
  useEffect(() => {
    syncDisplay(stateDisplay);
  }, [stateDisplay, syncDisplay]);

  // The stopwatch of the stage, as it stood when the panel opened; the room keeps it up to date
  const stateStopwatch = state?.stopwatch;
  useEffect(() => {
    syncStopwatch(stateStopwatch);
  }, [stateStopwatch, syncStopwatch]);

  // Like the draws: a reset nobody confirms goes back to being a plain button
  useEffect(() => {
    if (!resetting) return undefined;
    const timer = setTimeout(() => setResetting(false), CONFIRM_MS);
    return () => clearTimeout(timer);
  }, [resetting]);

  const runStopwatch = async (action) => {
    setTiming(true);
    setResetting(false);
    setTimingError(null);
    try {
      const { stopwatch: next, announced } = await updateStopwatch(adminKey, action);
      syncStopwatch(next);
      if (!announced) setTimingError('Quedó guardado, pero no se pudo avisar a la sala: los celulares lo ven al recargar');
    } catch (err) {
      if (isUnauthorized(err)) signOut();
      // Another panel got there first: the answer brings the stopwatch as it is now
      else if (err.response?.status === 409) syncStopwatch(err.response.data?.stopwatch);
      else setTimingError(`No se pudo: ${errorMessage(err)}. Probá de nuevo`);
    } finally {
      setTiming(false);
    }
  };

  const toggleDisplay = async (piece) => {
    setSwitching(piece);
    setError(null);
    try {
      const { display: next, announced } = await updateDisplay(adminKey, piece, !display[piece]);
      syncDisplay(next);
      if (!announced) setError('Quedó guardado, pero no se pudo avisar a la sala: los celulares lo ven al recargar');
    } catch (err) {
      if (isUnauthorized(err)) signOut();
      else setError(errorMessage(err));
    } finally {
      setSwitching(null);
    }
  };

  const buyers = state?.buyersJackpot?.count ?? 0;
  const stats = state?.stats;
  const stateDraws = state?.draws;
  const lastDraws = stateDraws ?? [];
  const muted = state?.blocked ?? [];

  // Until something happens live, the stage shows the last draw, like the phones do (the
  // stage here also says among how many clients the jackpot drew; the phones keep that out)
  const stage = useMemo(() => (stateDraws?.[0] ? withDraw(draws, drawFromRecord(stateDraws[0])) : draws), [draws, stateDraws]);

  // The range of the last jackpot comes back prefilled, until the admin types another
  useEffect(() => {
    if (rangeTouched.current) return;
    const last = (stateDraws ?? []).find((draw) => draw.game === 'jackpot');
    if (last) setRange({ from: last.from, to: last.to });
  }, [stateDraws]);

  const setRangeField = (field, value) => {
    rangeTouched.current = true;
    setRange((prev) => ({ ...prev, [field]: value === '' ? '' : Number(value) }));
  };
  // A draw launched while the draws are hidden still runs here, and its result reaches the chat
  const where = display.games
    ? 'en todos los celulares y en la pantalla del evento'
    : 'solo acá: los sorteos están ocultos para los usuarios'; // as long as the other, so the block keeps its height
  const rangeOk =
    Number.isInteger(range.from) && Number.isInteger(range.to) && range.from >= 1 && range.from < range.to && range.to <= MAX_NUMBER;

  const askConfirm = (game) => {
    setConfirming(game);
    clearTimeout(confirmTimer.current);
    confirmTimer.current = setTimeout(() => setConfirming(null), CONFIRM_MS);
  };

  // Both games go live on every phone: the panel says so if the room missed the announcement
  const launch = async (game, request) => {
    clearTimeout(confirmTimer.current);
    setConfirming(null);
    setLaunching(game);
    setError(null);
    try {
      const { announced } = await request();
      if (!announced) setError('El sorteo quedó guardado, pero no se pudo anunciar en la sala');
      refresh();
    } catch (err) {
      if (isUnauthorized(err)) signOut();
      else setError(errorMessage(err));
    } finally {
      setLaunching(null);
    }
  };
  const runJackpot = () => launch('jackpot', () => launchJackpot(adminKey, range));
  const runBuyers = () => launch('buyers', () => drawBuyer(adminKey));

  // Muting takes the chat away from the person; what they already said stays in the room
  const setLevel = async (participantId, nickname, level) => {
    try {
      await blockParticipant(adminKey, { participantId, nickname, level });
      refresh();
    } catch (err) {
      if (isUnauthorized(err)) signOut();
      else setError(errorMessage(err));
    }
  };

  const handleBlock = (message, level) => setLevel(message.userId, message.nickname, level);
  const handleUnmute = (person) => setLevel(person.participantId, person.nickname, 'none');

  const handleDelete = async (id) => {
    try {
      await deleteMessage(adminKey, id);
      dropMessage(id);
    } catch (err) {
      if (isUnauthorized(err)) signOut();
      else setError('No se pudo borrar el mensaje');
    }
  };

  // The chat was wiped for the next event: gone from the table, and from every phone that heard
  const handleChatCleared = ({ announced }) => {
    clearMessages();
    refresh();
    if (!announced) setError('El chat quedó borrado, pero no se pudo avisar a la sala: los celulares lo ven al recargar');
  };

  const handleAnnounce = async (e) => {
    e.preventDefault();
    const content = e.target.elements.notice.value.trim();
    if (!content || status !== 'connected') return;
    e.target.reset();
    try {
      await announce(content);
    } catch (err) {
      setError('No se pudo enviar el aviso');
    }
  };

  return (
    <div className='panel'>
      {/* The stage always shows both; each one says whether the phones see it too */}
      <section className='panel__stage'>
        {/* The two boards side by side, like on the phones when both show */}
        <div className='stage-boards'>
          <div className={`stage-piece${display.scoreboard ? '' : ' stage-piece--hidden'}`}>
            <Scoreboard scoreboard={scoreboard} />
            <div className='stage-piece__under'>
              <DisplayToggle
                name='el marcador'
                visible={display.scoreboard}
                busy={switching === 'scoreboard'}
                onToggle={() => toggleDisplay('scoreboard')}
              />
            </div>
          </div>
          <div className={`stage-piece${display.stopwatch ? '' : ' stage-piece--hidden'}`}>
            <Stopwatch stopwatch={stopwatch} />
            <div className='stage-piece__under'>
              {/* It runs from here: start (or go on), pause, and back to zero with a second tap */}
              <p className='stage-piece__controls'>
                <button
                  type='button'
                  className='btn-primary stage-piece__btn'
                  disabled={timing}
                  onClick={() => runStopwatch(stopwatch.running ? 'pause' : 'start')}
                >
                  {stopwatch.running ? 'Pausar' : stopwatch.elapsedMs > 0 ? 'Seguir' : 'Iniciar'}
                </button>
                {resetting ? (
                  <button type='button' className='btn-primary stage-piece__btn' disabled={timing} onClick={() => runStopwatch('reset')}>
                    Confirmar: a cero
                  </button>
                ) : (
                  <button
                    type='button'
                    className='admin__signout'
                    disabled={timing || (!stopwatch.running && stopwatch.elapsedMs === 0)}
                    onClick={() => setResetting(true)}
                  >
                    Resetear
                  </button>
                )}
              </p>
              {timingError && <p className='form-error stage-piece__error'>{timingError}</p>}
              <DisplayToggle
                name='el cronómetro'
                visible={display.stopwatch}
                busy={switching === 'stopwatch'}
                onToggle={() => toggleDisplay('stopwatch')}
              />
            </div>
          </div>
        </div>
        <div className={`stage-piece stage-piece--games${display.games ? '' : ' stage-piece--hidden'}`}>
          <Games draws={stage} participant={null} showTotal />
          <DisplayToggle
            name='los sorteos'
            visible={display.games}
            busy={switching === 'games'}
            onToggle={() => toggleDisplay('games')}
          />
        </div>
      </section>

      {/* What runs the show: the team games and the two jackpots */}
      <div className='panel__controls'>
        <ScoreboardControls adminKey={adminKey} scoreboard={scoreboard} onChange={syncScoreboard} onUnauthorized={signOut} />

        <section className='admin__block'>
          <h2 className='admin__block-title'>Jackpot</h2>
          <div className='range'>
            <label className='range__field'>
              del
              <input
                className='input range__input'
                type='number'
                inputMode='numeric'
                min='1'
                max={MAX_NUMBER}
                aria-label='Desde'
                value={range.from}
                onChange={(e) => setRangeField('from', e.target.value)}
              />
            </label>
            <label className='range__field'>
              al
              <input
                className='input range__input'
                type='number'
                inputMode='numeric'
                min='2'
                max={MAX_NUMBER}
                aria-label='Hasta'
                value={range.to}
                onChange={(e) => setRangeField('to', e.target.value)}
              />
            </label>
          </div>

          {confirming === 'jackpot' ? (
            <div className='panel__confirm'>
              <button type='button' className='btn-primary btn-big' disabled={Boolean(launching)} onClick={runJackpot}>
                Confirmar el jackpot
              </button>
              <button type='button' className='panel__cancel' onClick={() => setConfirming(null)}>
                Cancelar
              </button>
            </div>
          ) : (
            <button
              type='button'
              className='btn-primary btn-big'
              disabled={Boolean(launching) || !rangeOk}
              onClick={() => askConfirm('jackpot')}
            >
              {launching === 'jackpot' ? 'Sorteando…' : 'Lanzar el jackpot'}
            </button>
          )}
          <p className='panel__hint'>
            {rangeOk
              ? `Sale un número entre el ${pad(range.from)} y el ${pad(range.to)}, ${where}.`
              : `Los números van del 1 al ${MAX_NUMBER}, de menor a mayor.`}
          </p>
        </section>

        <section className='admin__block'>
          <h2 className='admin__block-title'>Jackpot de clientes</h2>
          {confirming === 'buyers' ? (
            <div className='panel__confirm'>
              <button type='button' className='btn-primary btn-big' disabled={Boolean(launching)} onClick={runBuyers}>
                Confirmar el sorteo
              </button>
              <button type='button' className='panel__cancel' onClick={() => setConfirming(null)}>
                Cancelar
              </button>
            </div>
          ) : (
            <button
              type='button'
              className='btn-primary btn-big'
              disabled={Boolean(launching) || !buyers}
              onClick={() => askConfirm('buyers')}
            >
              {launching === 'buyers' ? 'Sorteando…' : 'Sortear un cliente'}
            </button>
          )}
          <p className='panel__hint'>
            {buyers
              ? `Sortea un nombre entre ${buyers} clientes que compraron, ${where}.`
              : 'Todavía no hay clientes con compras.'}
          </p>
        </section>

        {error && <p className='form-error'>{error}</p>}
      </div>

      <ReviewQueue
        adminKey={adminKey}
        reviewWindowSeconds={state?.reviewWindowSeconds}
        published={messages}
        status={status}
        onUnauthorized={signOut}
        onStateChange={refresh}
        onDelete={handleDelete}
        onPhotoRemoved={dropPhoto}
        onBlock={handleBlock}
        onChatCleared={handleChatCleared}
        emotes={emotes}
      />

      {/* What happened so far, who is muted and a word to the room: nothing here goes live on a tap */}
      <div className='panel__info'>
        <section className='admin__block'>
          <h2 className='admin__block-title'>En números</h2>
          <div className='tiles'>
            <div className='tile'>
              <span className='tile__label'>Participantes</span>
              <span className='tile__value'>{stats?.participants ?? '—'}</span>
            </div>
            <div className='tile'>
              <span className='tile__label'>Escribieron</span>
              <span className='tile__value'>{stats?.writers ?? '—'}</span>
            </div>
            <div className='tile'>
              <span className='tile__label'>Mensajes</span>
              <span className='tile__value'>{stats?.messages ?? '—'}</span>
            </div>
            <div className='tile'>
              <span className='tile__label'>Compradores</span>
              <span className='tile__value'>{stats?.buyers ?? '—'}</span>
            </div>
            <div className='tile'>
              <span className='tile__label'>Sorteos</span>
              <span className='tile__value'>{stats?.draws ?? '—'}</span>
            </div>
            <div className='tile'>
              <span className='tile__label'>Silenciados</span>
              <span className='tile__value'>{stats?.blocked ?? '—'}</span>
            </div>
          </div>
        </section>

        <section className='admin__block'>
          <h2 className='admin__block-title'>Últimos sorteos</h2>
          {lastDraws.length ? (
            <ul className='admin__list'>
              {lastDraws.map((draw) => (
                <li key={draw.id} className='admin__row'>
                  <span className='admin__row-main'>
                    {draw.game === 'jackpot' ? `🎰 ${pad(draw.number)}` : `🎉 ${draw.winner}`}
                  </span>
                  <span className='admin__row-side'>
                    {draw.game === 'jackpot' ? `del ${pad(draw.from)} al ${pad(draw.to)}` : `entre ${draw.total}`} ·{' '}
                    {time(draw.createdAt)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className='admin__empty'>Todavía no hubo sorteos.</p>
          )}
        </section>

        <section className='admin__block'>
          <h2 className='admin__block-title'>Silenciados</h2>
          {muted.length ? (
            <ul className='admin__list'>
              {muted.map((person) => (
                <li key={person.participantId} className='admin__row'>
                  <span className='admin__row-main'>{person.nickname}</span>
                  <span className='admin__row-side'>
                    {time(person.blockedAt)} ·{' '}
                    <button type='button' className='link-action' onClick={() => handleUnmute(person)}>
                      dejar escribir
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className='admin__empty'>Nadie silenciado. Silenciar solo saca el chat: la persona sigue viendo y entra en los sorteos.</p>
          )}
        </section>

        <form className='composer panel__notice' onSubmit={handleAnnounce}>
          <input className='input' type='text' name='notice' maxLength={500} placeholder='Escribir como Bolivia Fitness…' />
          <button type='submit' className='btn-primary' disabled={status !== 'connected'}>
            Enviar
          </button>
        </form>
      </div>
    </div>
  );
};

const AdminPanel = ({ emotes = [] }) => {
  const [adminKey, setAdminKey] = useState(loadAdminKey);
  const [authed, setAuthed] = useState(false);
  const [draftKey, setDraftKey] = useState('');
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [view, setView] = useState('panel');

  const signOut = useCallback(() => {
    clearAdminKey();
    setAdminKey('');
    setAuthed(false);
    setState(null);
  }, []);

  const refresh = useCallback(
    async (key = adminKey) => {
      try {
        setState(await fetchPanelState(key));
        // Only now, with a key that works: the panel never mounts without one
        saveAdminKey(key);
        setAuthed(true);
        setError(null);
        return true;
      } catch (err) {
        if (isUnauthorized(err)) {
          signOut();
          setError('Clave incorrecta');
        } else {
          setError(errorMessage(err));
        }
        return false;
      }
    },
    [adminKey, signOut]
  );

  useEffect(() => {
    if (!adminKey) return undefined;
    refresh(adminKey);
    const interval = setInterval(() => refresh(adminKey), REFRESH_MS);
    return () => clearInterval(interval);
  }, [adminKey, refresh]);

  // Signing in only stores the key; the effect below is what checks it and opens the panel
  const handleSignIn = (e) => {
    e.preventDefault();
    const key = draftKey.trim();
    if (!key) return;
    setDraftKey('');
    setAdminKey(key);
  };

  if (!authed || !adminKey) {
    return (
      <div className='admin-login'>
        <form className='admin-login__card' onSubmit={handleSignIn}>
          <h1 className='admin-login__title'>Panel de control</h1>
          <p className='admin-login__subtitle'>Ingresá la clave de administración del evento.</p>
          <div className='field'>
            <label htmlFor='admin-key'>Clave</label>
            <input
              id='admin-key'
              className='input'
              type='password'
              autoComplete='current-password'
              value={draftKey}
              onChange={(e) => setDraftKey(e.target.value)}
            />
          </div>
          {error && <p className='form-error'>{error}</p>}
          <button type='submit' className='btn-primary' disabled={!draftKey.trim()}>
            Entrar
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className='admin'>
      <header className='admin__header'>
        <div>
          <p className='admin__brand'>Bolivia Fitness</p>
          <h1>Panel de control</h1>
        </div>
        <div className='admin__header-actions'>
          <button type='button' className='admin__signout' onClick={() => setView(view === 'users' ? 'panel' : 'users')}>
            {view === 'users' ? '← Volver al panel' : 'Usuarios'}
          </button>
          <a className='admin__signout' href='/'>
            Ver el chat
          </a>
          <button type='button' className='admin__signout' onClick={signOut}>
            Cerrar sesión
          </button>
        </div>
      </header>

      {view === 'users' ? (
        <UsersReport adminKey={adminKey} onUnauthorized={signOut} />
      ) : (
        <Panel
          adminKey={adminKey}
          state={state}
          refresh={refresh}
          signOut={signOut}
          error={error}
          setError={setError}
          emotes={emotes}
        />
      )}
    </div>
  );
};

export default AdminPanel;
